import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { NexaBootSplash } from '@/components/nexa-mark/nexa-boot-splash';

export function LoginPage() {
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
  const [params] = useSearchParams();
  const reason = params.get('reason');
  const { t } = useTranslation();

  // An Auth0 organization invitation lands here as
  // `/login?invitation=<ticket>&organization=<org_id>&organization_name=…`.
  // The ticket must ride on the authorize request or the invitee gets a plain
  // login with no way to accept. The organization stays the one this build is
  // pinned to (auth-provider); a link for another org is not forwarded.
  const invitation = params.get('invitation');

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      void loginWithRedirect(invitation ? { authorizationParams: { invitation } } : undefined);
    }
  }, [isLoading, isAuthenticated, loginWithRedirect, invitation]);

  const captionKey = reason ? captionKeyForReason(reason) : null;

  return (
    <NexaBootSplash
      label={t('nexa.signingIn')}
      caption={captionKey ? t(captionKey) : undefined}
    />
  );
}

// Messages keyed to the LogoutReason values from src/auth/force-logout.ts.
// Per docs/ui/auth0-frontend-tds.md §12, reason context lets the login page
// render appropriate copy. Returns a translation KEY (the copy lives in the
// `admin` bundle under `auth.reason.*`) — null when the reason carries no copy.
function captionKeyForReason(reason: string): string | null {
  switch (reason) {
    case 'token-rejected':
    case 'ws-token-rejected':
    case 'sse-token-rejected':
      return 'admin:auth.reason.sessionEnded';
    case 'idle-timeout':
      return 'admin:auth.reason.idleTimeout';
    case 'wrong-tenant':
      return 'admin:auth.reason.wrongTenant';
    default:
      return null;
  }
}
